import { useContext, useState, useEffect, useCallback } from 'react';
import MainLayout from '../../components/MainLayout';
import { FlowForm, FlowFormLeaveContext, List, ListItem, ButtonBar, useFlowFormGlobal } from 't-lib-ui/structure';
import { TextButton, Note } from 't-lib-ui/widgets';
import { addIcon, refreshIcon } from '../../components/icons';
import AdminSecretModal from '../../components/admin/AdminSecretModal';
import AdminHeader from '../../components/admin/AdminHeader';
import { sidebarLeaveHandler } from '../../components/LeftSidebarFocus';
import { RouterContext, goTo } from 't-lib-ui/router';
import { AppState } from '../../context/general';
import { nonNull } from 't-lib-ui/helpers';
import { adminListInvitations } from '../../connector/api';
import { RestInvitation } from '../../schema-copy/auth-schema';
import { planDisplayNames } from '../../schema-copy/userPlans';

const formatDate = (iso?: string) => iso ? new Date(iso).toLocaleDateString() : 'never';

const RenderInvitation: React.FC<{ invitation: RestInvitation }> = ({ invitation }) => (
  <ListItem focussable="flow" href={`/a/admin/invitation/${invitation.id}`}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 500 }}>
          {invitation.name}
          {invitation.isDefault && <span style={{ marginLeft: '8px', fontSize: '0.75em', color: '#fff', background: 'var(--gray-500)', padding: '2px 6px', borderRadius: '4px' }}>DEFAULT</span>}
        </div>
        <div style={{ fontSize: '0.85em', color: 'var(--gray-400)', display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '4px' }}>
          <span style={{ whiteSpace: 'nowrap' }}>code: {invitation.code || '—'}</span>
          <span style={{ whiteSpace: 'nowrap' }}>•</span>
          <span style={{ whiteSpace: 'nowrap' }}>plan: {(planDisplayNames as Record<string, string>)[invitation.planCode] || invitation.planCode}</span>
          <span style={{ whiteSpace: 'nowrap' }}>•</span>
          <span style={{ whiteSpace: 'nowrap' }}>{invitation.capacity} left</span>
          <span style={{ whiteSpace: 'nowrap' }}>•</span>
          <span style={{ whiteSpace: 'nowrap' }}>expires: {formatDate(invitation.expiresAt)}</span>
        </div>
      </div>
    </div>
  </ListItem>
);

const AdminInvitationsPage: React.FC = () => {
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  const [adminSecret, setAdminSecret] = useState<string | null>(null);
  const [authFailed, setAuthFailed] = useState(0);
  const [invitations, setInvitations] = useState<RestInvitation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  useFlowFormGlobal();

  const load = useCallback(async (secret: string) => {
    try {
      setLoading(true);
      setError(undefined);
      setInvitations(await adminListInvitations(nonNull(appState.state.authData), secret));
    } catch (e: any) {
      if (e?.status == 401) setAuthFailed(n => n + 1);
      setError(e instanceof Error ? e.message : 'Failed to load invitations');
    } finally {
      setLoading(false);
    }
  }, [appState.state.authData]);

  useEffect(() => {
    if (adminSecret) load(adminSecret);
  }, [adminSecret, load]);

  return (
    <>
      <AdminSecretModal onSecretVerified={setAdminSecret} authFailed={authFailed} />
      {adminSecret && (
        <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
          <MainLayout
            selectedFeed="admin-invitations"
            right={({ onExpand }) => (
              <FlowForm main={true} className='full-margin'>
                <AdminHeader title="Invitations" onExpand={onExpand} />
                <ButtonBar size="small">
                  <TextButton
                    label="Create invitation"
                    icon={addIcon}
                    onClick={() => goTo(routerCtx, '/a/admin/invitation/new')}
                    focussable="flow"
                    type="major"
                  />
                  <TextButton
                    label="Refresh"
                    icon={refreshIcon}
                    onClick={() => adminSecret && load(adminSecret)}
                    focussable="flow"
                    busy={loading}
                  />
                </ButtonBar>

                {error && <Note type='error'>{error}</Note>}

                <List>
                  {invitations.map(invitation => <RenderInvitation key={invitation.id} invitation={invitation} />)}
                  {!loading && !invitations.length && !error && (
                    <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-500)' }}>
                      No invitations yet.
                    </div>
                  )}
                </List>
              </FlowForm>
            )}
            startWithLeftCollapsed={false}
          />
        </FlowFormLeaveContext.Provider>
      )}
    </>
  );
};

export default AdminInvitationsPage;
